'use client';

import React, { useRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { Input } from '../Input';
import { InputProps } from '@/types/components';
import { CalendarIcon } from '@/components/icons';

export interface DatePickerProps extends Omit<InputProps, 'type' | 'onChange'> {
  /** Date value (YYYY-MM-DD format) */
  value?: string;
  /** Callback when date changes */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Minimum date (YYYY-MM-DD format) */
  min?: string;
  /** Maximum date (YYYY-MM-DD format) */
  max?: string;
}

export interface DatePickerRef {
  showPicker: () => void;
}

const ClickableIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  pointer-events: auto;
  
  &:hover {
    opacity: 0.7;
  }
`;

/**
 * DatePicker Component
 * 
 * A simple date picker using native HTML5 date input with clickable calendar icon
 * 
 * @component
 * @example
 * ```tsx
 * <DatePicker
 *   label="Start Date"
 *   value={startDate}
 *   onChange={(e) => setStartDate(e.target.value)}
 *   min="2025-01-01"
 *   max="2025-12-31"
 * />
 * ```
 */
export const DatePicker = React.forwardRef<DatePickerRef, DatePickerProps>(({
  value,
  onChange,
  min,
  max,
  id,
  onClick,
  ...inputProps
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const showPicker = () => {
    if (inputRef.current) {
      // Use showPicker() if available (modern browsers), otherwise fallback to click()
      if ('showPicker' in inputRef.current && typeof (inputRef.current as HTMLInputElement).showPicker === 'function') {
        (inputRef.current as HTMLInputElement).showPicker();
      } else {
        inputRef.current.click();
      }
    }
  };

  // Expose showPicker method via ref
  useImperativeHandle(ref, () => ({
    showPicker,
  }));

  const handleIconClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    showPicker();
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // Call onClick prop if provided
    onClick?.(e);
    // Open picker when input is clicked
    showPicker();
  };

  return (
    <Input
      ref={inputRef}
      type="date"
      value={value || ''}
      onChange={onChange}
      onClick={handleInputClick}
      id={id}
      endAdornment={
        <ClickableIconWrapper onClick={handleIconClick} role="button" aria-label="Open date picker">
          <CalendarIcon size={20} />
        </ClickableIconWrapper>
      }
      {...(min && { min })}
      {...(max && { max })}
      {...inputProps}
    />
  );
});

DatePicker.displayName = 'DatePicker';

export default DatePicker;

